// ╔══════════════════════════════════════════════════════════════╗
// ║  Production Entry — by employee (not pre-registered workers)  ║
// ║                                                                ║
// ║  Eng picks ANY employee + stage + actual production.          ║
// ║  Backend auto-creates production_worker if needed.            ║
// ║  Same employee can be on different stage each day.            ║
// ╚══════════════════════════════════════════════════════════════╝

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getErrorMessage } from '../../lib/api.js';
import { useToast } from '../../components/Toast.jsx';
import { fmtAR } from '../../lib/format.js';
import {
  Avatar,
  Badge,
  Btn,
  Card,
  Chip,
  Field,
  Input,
  Select,
} from '../../components/ui.jsx';
import Icon from '../../components/Icon.jsx';

export default function ProductionEntryPage() {
  const toast = useToast();
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [deptFilter, setDeptFilter] = useState('all');
  const [search, setSearch] = useState('');

  // ─ Queries ─
  const { data: empData, isLoading: empLoading } = useQuery({
    queryKey: ['employees', 'all-active'],
    queryFn: () =>
      api.get('/employees', { params: { pageSize: 1000 } }).then((r) => r.data),
  });
  const { data: entriesData } = useQuery({
    queryKey: ['production', 'entries', { date }],
    queryFn: () =>
      api.get('/production/entries', { params: { date, pageSize: 1000 } }).then((r) => r.data),
  });
  const { data: stagesData } = useQuery({
    queryKey: ['production', 'stages'],
    queryFn: () => api.get('/production/stages').then((r) => r.data),
  });
  const { data: deptsData } = useQuery({
    queryKey: ['departments'],
    queryFn: () => api.get('/departments').then((r) => r.data),
  });
  // Phase K: pull all production workers to look up each employee's saved
  // daily target (set in the Workers management screen).
  const { data: workersData } = useQuery({
    queryKey: ['production', 'workers', 'targets'],
    queryFn: () =>
      api
        .get('/production/workers', { params: { period: 'day' } })
        .then((r) => r.data),
  });

  // ─ Mutation: create/update entry ─
  const submitMut = useMutation({
    mutationFn: ({ employeeId, stageId, actual }) =>
      api.post('/production/entries', { employeeId, stageId, date, actual }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production', 'entries'] });
      qc.invalidateQueries({ queryKey: ['production', 'workers'] });
      toast.push({ tone: 'success', title: 'تم تسجيل الإنتاج' });
    },
    onError: (err) =>
      toast.push({ tone: 'danger', title: 'فشل التسجيل', body: getErrorMessage(err) }),
  });

  // ─ Data preparation ─
  const allEmployees = empData?.employees ?? [];
  const stages = stagesData?.stages ?? [];
  const departments = deptsData?.departments ?? [];
  const entries = entriesData?.entries ?? [];

  const entryByEmployee = useMemo(
    () => Object.fromEntries(entries.filter((e) => e.employeeId).map((e) => [e.employeeId, e])),
    [entries],
  );
  const stageMap = useMemo(
    () => Object.fromEntries(stages.map((s) => [s.id, s])),
    [stages],
  );
  // employeeId → workers' dailyTarget (from Workers management screen)
  const workerTargetByEmployee = useMemo(() => {
    const m = {};
    for (const w of workersData?.workers ?? []) {
      if (w.employeeId) m[w.employeeId] = w.dailyTarget;
    }
    return m;
  }, [workersData]);

  // Filter employees
  const employees = allEmployees.filter((e) => {
    if (e.status === 'terminated') return false;
    if (deptFilter !== 'all' && e.deptId !== deptFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const matches =
        e.name.toLowerCase().includes(q) ||
        e.nameEnglish?.toLowerCase().includes(q) ||
        e.id.toLowerCase().includes(q);
      if (!matches) return false;
    }
    return true;
  });

  // Stats for today
  const recorded = employees.filter((e) => entryByEmployee[e.id]).length;
  const remaining = employees.length - recorded;
  const hits = employees.filter(
    (e) => entryByEmployee[e.id] && Number(entryByEmployee[e.id].delta) > 0,
  ).length;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 fade-up">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-brand-500 font-bold">
            خط الإنتاج
          </div>
          <h1 className="text-2xl md:text-3xl font-black mt-1">تسجيل إنتاج اليوم</h1>
          <p className="text-sm text-ink-400 mt-1">
            اختار المرحلة لكل موظف اليوم — العامل ممكن يتنقّل بين المراحل كل يوم.
          </p>
        </div>
        <Field label="اليوم">
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-44"
          />
        </Field>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="إجمالى الموظفين" value={fmtAR(employees.length)} tone="neutral" />
        <SummaryCard label="تم تسجيلهم" value={fmtAR(recorded)} tone="brand" />
        <SummaryCard label="حقّقوا الهدف" value={fmtAR(hits)} tone="success" />
        <SummaryCard label="باقى" value={fmtAR(remaining)} tone={remaining > 0 ? 'warn' : 'success'} />
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Icon name="search" className="absolute top-3 right-3 text-ink-400" size={16} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="ابحث بالاسم أو الكود…"
              className="w-full h-10 px-4 pe-10 rounded-xl bg-ink-50 dark:bg-ink-800 ring-1 ring-ink-200 dark:ring-ink-700 focus:ring-2 focus:ring-brand-400 outline-none text-sm"
            />
          </div>
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
            <Chip active={deptFilter === 'all'} onClick={() => setDeptFilter('all')}>
              كل الأقسام
            </Chip>
            {departments.map((d) => (
              <Chip
                key={d.id}
                active={deptFilter === d.id}
                onClick={() => setDeptFilter(d.id)}
              >
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: d.color }} />
                  {d.nameAr}
                </span>
              </Chip>
            ))}
          </div>
        </div>
        <div className="mt-3 text-[11px] text-ink-400">
          💡 المرحلة بتتسجّل لكل يوم على حدة — الموظف ممكن يبقى فى مرحلة مختلفة كل يوم.
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 dark:bg-ink-800/40 text-xs text-ink-500">
              <tr>
                <th className="text-start font-semibold px-4 py-3 whitespace-nowrap">الموظف</th>
                <th className="text-start font-semibold px-4 py-3 whitespace-nowrap">القسم</th>
                <th className="text-start font-semibold px-4 py-3 whitespace-nowrap w-64">المرحلة اليوم</th>
                <th className="text-center font-semibold px-4 py-3 whitespace-nowrap">الهدف</th>
                <th className="text-center font-semibold px-4 py-3 whitespace-nowrap w-56">الكمية الفعلية</th>
                <th className="text-start font-semibold px-4 py-3 whitespace-nowrap">الحالة</th>
              </tr>
            </thead>
            <tbody className="divide-soft">
              {empLoading && (
                <tr><td colSpan="6" className="p-8 text-center text-ink-400">جاري التحميل…</td></tr>
              )}
              {!empLoading && employees.length === 0 && (
                <tr><td colSpan="6" className="p-8 text-center text-ink-400">لا توجد موظفون</td></tr>
              )}
              {employees.map((e) => (
                <EmployeeRow
                  key={e.id}
                  employee={e}
                  entry={entryByEmployee[e.id]}
                  stages={stages}
                  stageMap={stageMap}
                  departments={departments}
                  workerTarget={workerTargetByEmployee[e.id]}
                  onSubmit={(stageId, actual) =>
                    submitMut.mutate({ employeeId: e.id, stageId, actual })
                  }
                  submitting={submitMut.isPending}
                />
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function SummaryCard({ label, value, tone = 'neutral' }) {
  const tones = {
    neutral: 'bg-ink-50 dark:bg-ink-800/40',
    brand: 'bg-brand-50 dark:bg-brand-950/30',
    success: 'bg-emerald-50 dark:bg-emerald-950/30',
    warn: 'bg-amber-50 dark:bg-amber-950/30',
  };
  return (
    <div className={`p-4 rounded-2xl ${tones[tone]} ring-1 ring-ink-100 dark:ring-ink-800`}>
      <div className="text-xs text-ink-400 font-bold uppercase tracking-wider">{label}</div>
      <div className="text-2xl font-black mt-1 tabular">{value}</div>
    </div>
  );
}

function EmployeeRow({ employee, entry, stages, stageMap, departments, workerTarget, onSubmit, submitting }) {
  const [val, setVal] = useState('');
  const [selectedStageId, setSelectedStageId] = useState('');
  const [editing, setEditing] = useState(false);

  // When entry exists, lock stage to entry's stage (until editing)
  const displayStageId = editing ? selectedStageId : (entry?.stageId ?? selectedStageId);
  const dept = departments.find((d) => d.id === employee.deptId);

  function handleSubmit() {
    if (!selectedStageId) return;
    const n = Number(val);
    if (!Number.isFinite(n) || n < 0) return;
    onSubmit(selectedStageId, n);
    setVal('');
    setEditing(false);
  }

  function increment(delta) {
    const current = Number(val) || 0;
    const next = Math.max(0, current + delta);
    setVal(String(next));
  }

  const currentStage = stageMap[displayStageId];
  // Phase K: Use the worker's saved daily target (from Workers management).
  // If the entry is already recorded, prefer the snapshotted target on the
  // entry itself (so historical days keep their own target). Fall back to
  // the stage's default daily target if neither is set.
  const dailyTarget =
    entry?.target ??
    workerTarget ??
    currentStage?.dailyTarget ??
    0;
  const status = entry?.status;
  const hit = entry && Number(entry.delta) > 0;
  const canEdit = entry && entry.status !== 'approved';
  const showInput = !entry || editing;

  return (
    <tr className="hover:bg-ink-50 dark:hover:bg-ink-800/40">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <Avatar name={employee.name} size={32} />
          <div>
            <div className="font-semibold text-sm">{employee.name}</div>
            <div className="text-[10px] text-ink-400 font-mono">
              {employee.id}
              {employee.nameEnglish && <span> · {employee.nameEnglish}</span>}
            </div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        {dept && (
          <span className="text-[11px] text-ink-500 inline-flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: dept.color }} />
            {dept.nameAr}
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        {entry && !editing ? (
          stageMap[entry.stageId] ? (
            <span
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] font-bold"
              style={{
                backgroundColor: `${stageMap[entry.stageId].color}20`,
                color: stageMap[entry.stageId].color,
              }}
            >
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: stageMap[entry.stageId].color }} />
              {stageMap[entry.stageId].sortOrder}. {stageMap[entry.stageId].nameAr}
            </span>
          ) : (
            <span className="text-[11px] text-ink-400">—</span>
          )
        ) : (
          <Select
            value={selectedStageId}
            onChange={(e) => setSelectedStageId(e.target.value)}
            className="h-9 text-sm"
          >
            <option value="">— اختر المرحلة —</option>
            {stages.map((s) => (
              <option key={s.id} value={s.id}>
                {s.sortOrder}. {s.nameAr}
              </option>
            ))}
          </Select>
        )}
      </td>
      <td className="px-4 py-3 text-center font-mono tabular font-bold">
        {dailyTarget > 0 ? dailyTarget : '—'}
      </td>
      <td className="px-4 py-3">
        {entry && !editing ? (
          <div
            className={`text-center font-extrabold text-xl tabular ${
              hit ? 'text-emerald-600' : 'text-rose-600'
            }`}
          >
            {fmtAR(entry.actual)}
          </div>
        ) : (
          <div className="flex items-center gap-1 justify-center">
            <button
              type="button"
              onClick={() => increment(-5)}
              className="w-8 h-9 grid place-items-center rounded-lg bg-ink-100 dark:bg-ink-800 hover:bg-ink-200 dark:hover:bg-ink-700 font-bold"
            >
              −
            </button>
            <input
              type="number"
              inputMode="numeric"
              min="0"
              value={val}
              onChange={(e) => setVal(e.target.value)}
              placeholder="0"
              className="h-9 w-20 text-center rounded-lg bg-ink-50 dark:bg-ink-800 ring-1 ring-ink-200 dark:ring-ink-700 focus:ring-2 focus:ring-brand-400 outline-none font-mono font-bold tabular"
              autoFocus={editing}
            />
            <button
              type="button"
              onClick={() => increment(5)}
              className="w-8 h-9 grid place-items-center rounded-lg bg-ink-100 dark:bg-ink-800 hover:bg-ink-200 dark:hover:bg-ink-700 font-bold"
            >
              +
            </button>
            <Btn
              size="sm"
              variant="primary"
              icon="check"
              disabled={!val || !selectedStageId || submitting}
              onClick={handleSubmit}
              className="mx-1"
            >
              {entry ? 'حفظ' : 'سجّل'}
            </Btn>
            {editing && (
              <Btn
                size="sm"
                variant="ghost"
                onClick={() => {
                  setEditing(false);
                  setVal('');
                  setSelectedStageId('');
                }}
              >
                إلغاء
              </Btn>
            )}
          </div>
        )}
      </td>
      <td className="px-4 py-3">
        {entry && !editing ? (
          <div className="inline-flex items-center gap-2">
            <Badge
              tone={status === 'approved' ? 'success' : status === 'rejected' ? 'danger' : 'warn'}
              dot
            >
              {status === 'approved' ? 'معتمد' : status === 'rejected' ? 'مرفوض' : 'تحت المراجعة'}
            </Badge>
            {canEdit && (
              <button
                onClick={() => {
                  setVal(String(entry.actual));
                  setSelectedStageId(entry.stageId);
                  setEditing(true);
                }}
                className="text-[11px] text-brand-500 hover:underline font-bold"
              >
                تعديل
              </button>
            )}
          </div>
        ) : (
          <span className="text-[11px] text-ink-400">— لم يُسجَّل</span>
        )}
      </td>
    </tr>
  );
}
